import React, { useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import ClientLayout from '../../../components/layout/ClientLayout';
import Spinner from '../../../components/ui/Spinner';
import Alert from '../../../components/ui/Alert';
import { listarUnidades } from '../../../api/units.api';
import { useFirestoreQuery } from '../../../hooks/useFirestoreQuery';
import { IUnidade as FirestoreUnidade } from '../../../types/firestore.types';

// Fix para ícones padrão do Leaflet
const iconRetinaUrl = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png';
const iconUrl = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png';
const shadowUrl = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png';

// Configurar ícones padrão
const DefaultIcon = L.icon({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Ícone customizado para restaurantes
const restaurantIcon = new L.Icon({
  iconUrl: 'https://maps.google.com/mapfiles/ms/icons/restaurant.png',
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});

interface UnidadeComCoordenadas extends FirestoreUnidade {
  latitude: number;
  longitude: number;
}

const mapContainerStyle = {
  width: '100%',
  height: 'calc(100vh - 120px)',
  borderRadius: '8px',
};

const center = [-22.000, -47.000] as [number, number];

// Componente para controlar a visualização do mapa
const MapController: React.FC<{ unidades: UnidadeComCoordenadas[] }> = ({ unidades }) => {
  const map = useMap();

  React.useEffect(() => {
    if (unidades.length > 0) {
      const bounds = L.latLngBounds(unidades.map(unit => [unit.latitude, unit.longitude]));
      map.fitBounds(bounds, { padding: [20, 20] });
    } else {
      map.setView(center, 10);
    }
  }, [map, unidades]);

  return null;
};

const UnitsMapPage: React.FC = () => {
  const { data: unidades, isLoading, error } = useFirestoreQuery<FirestoreUnidade>(
    'unidades',
    listarUnidades
  );

  const [selectedUnit, setSelectedUnit] = useState<UnidadeComCoordenadas | null>(null);

  // Simulação de coordenadas para as unidades
  const unidadesComCoordenadas: UnidadeComCoordenadas[] = useMemo(() => {
    if (!unidades) return [];
    const fixedCoordinates = [
      { lat: -22.000, lng: -47.000 },
      { lat: -22.050, lng: -47.100 },
      { lat: -21.950, lng: -46.900 },
      { lat: -22.100, lng: -47.020 },
      { lat: -21.880, lng: -47.080 },
    ];

    return unidades.map((unidade, index) => ({
      ...unidade,
      latitude: fixedCoordinates[index % fixedCoordinates.length].lat,
      longitude: fixedCoordinates[index % fixedCoordinates.length].lng,
    }));
  }, [unidades]);

  if (isLoading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center h-full p-8">
          <Spinner size="lg" color="primary" />
          <p className="ml-4 text-text">Carregando mapa...</p>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="flex flex-col lg:flex-row h-full w-full p-4 md:p-8 bg-background text-text">
        <div className="lg:w-2/3 w-full h-96 lg:h-auto relative rounded-lg shadow-xl overflow-hidden">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background bg-opacity-75 z-10">
              <Spinner size="lg" color="primary" />
              <p className="ml-4 text-text">Buscando unidades...</p>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-background bg-opacity-75 z-10">
              <Alert tipo="error" mensagem={`Erro ao carregar unidades: ${error.message}`} />
            </div>
          )}
          
          <MapContainer
            center={center}
            zoom={10}
            style={mapContainerStyle}
            className="rounded-lg"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <MapController unidades={unidadesComCoordenadas} />
            
            {unidadesComCoordenadas.map((unidade) => (
              <Marker
                key={unidade.id}
                position={[unidade.latitude, unidade.longitude]}
                eventHandlers={{
                  click: () => setSelectedUnit(unidade),
                }}
                icon={restaurantIcon}
              >
                <Popup
                  onClose={() => setSelectedUnit(null)}
                  className="custom-popup"
                >
                  <div className="p-4 bg-background text-text rounded-lg shadow-lg border border-primary/20">
                    <h3 className="text-lg font-semibold text-primary mb-2">{unidade.nome}</h3>
                    <p className="text-sm text-text-light">{unidade.endereco}</p>
                    <button
                      onClick={() => alert(`Detalhes da unidade: ${unidade.nome}`)}
                      className="mt-3 px-4 py-2 bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663] text-white rounded-md hover:bg-primary transition-colors duration-300 text-sm"
                    >
                      Ver Detalhes
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <div className="lg:w-1/3 w-full lg:ml-8 mt-8 lg:mt-0 p-6 bg-background-dark rounded-lg shadow-xl overflow-y-auto max-h-[calc(100vh-120px)]">
          <h2 className="text-2xl font-bold text-primary mb-6 border-b-2 border-primary/20 pb-2">Nossas Unidades</h2>
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Spinner size="md" color="secondary" />
              <p className="ml-3 text-text">Carregando lista...</p>
            </div>
          )}
          {error && (
            <Alert tipo="error" mensagem={`Não foi possível carregar a lista de unidades: ${error.message}`} />
          )}
          {!isLoading && !error && unidadesComCoordenadas.length === 0 && (
            <p className="text-text-light text-center py-8">Nenhuma unidade encontrada.</p>
          )}
          <ul className="space-y-4">
            {unidadesComCoordenadas.map((unidade) => (
              <li
                key={unidade.id}
                className="bg-background-light p-4 rounded-md shadow-md cursor-pointer hover:bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663]-dark transition-colors duration-300 border border-gray-700"
                onClick={() => setSelectedUnit(unidade)}
              >
                <h3 className="text-lg font-semibold text-primary">{unidade.nome}</h3>
                <p className="text-sm text-text-light mt-1">{unidade.endereco}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </ClientLayout>
  );
};

export default UnitsMapPage;